import { useState, useEffect, createContext, useContext, ReactNode } from 'react'
import { useAuth } from '@/hooks/use-auth'
import { orgDataClient } from '@/lib/employees'

// Single source of truth for access lives in Postgres. The runtime calls
// public.user_access_for(email) — a SECURITY DEFINER function that always
// returns one row: menus from public.user_sections, admins auto-expanded to
// all tabs. Admin (Total admin) is a per-person setting on
// user_sections.is_admin, set on Settings -> Access. Financial admin
// (payroll) is a per-person setting too, user_sections.has_payroll_access
// (needs is_admin) — no job role grants it any more.
// Never query user_sections / confidential_access / financial_admin_access
// directly from new code — call user_access_for() or read the
// public.user_access view (inspection).

interface TabAccessState {
  allowedTabs: string[]
  isAdmin: boolean
  isFinancialAdmin: boolean
  projectIds: number[]
  isLoading: boolean
  diagnostics?: {
    email?: string
    error?: string
  }
}

const TabAccessContext = createContext<TabAccessState>({
  allowedTabs: [],
  isAdmin: false,
  isFinancialAdmin: false,
  projectIds: [],
  isLoading: true,
})

export function TabAccessProvider({ children }: { children: ReactNode }) {
  const { auth } = useAuth()
  const [state, setState] = useState<TabAccessState>({
    allowedTabs: [],
    isAdmin: false,
    isFinancialAdmin: false,
    projectIds: [],
    isLoading: true,
  })

  useEffect(() => {
    if (auth.isLoading) return
    if (!auth.user?.email) {
      setState({
        allowedTabs: [], isAdmin: false, isFinancialAdmin: false, projectIds: [],
        isLoading: false, diagnostics: { error: 'no-auth-user' },
      })
      return
    }
    const email = auth.user.email.toLowerCase()

    let cancelled = false
    ;(async () => {
      const { data, error } = await orgDataClient
        .rpc('user_access_for', { p_email: email })
        .single()
      if (cancelled) return

      const row = data as {
        is_admin: boolean
        is_financial_admin: boolean
        tabs: string[]
        project_ids: number[]
      } | null

      setState({
        allowedTabs: (row?.tabs ?? []) as string[],
        isAdmin: !!row?.is_admin,
        isFinancialAdmin: !!row?.is_financial_admin,
        projectIds: (row?.project_ids ?? []) as number[],
        isLoading: false,
        diagnostics: { email, error: error?.message },
      })
    })()
    return () => { cancelled = true }
  }, [auth.isLoading, auth.user?.email])

  return (
    <TabAccessContext.Provider value={state}>
      {children}
    </TabAccessContext.Provider>
  )
}

export function useTabAccess() {
  return useContext(TabAccessContext)
}

/**
 * Maps a pathname to a tab name.
 * e.g. /team-assignments/123 -> "team-assignments"
 */
export function pathnameToTab(pathname: string): string | null {
  const segment = pathname.split('/').filter(Boolean)[0]
  return segment || null
}

export function useHasTabAccess(tabName: string): { hasAccess: boolean; isLoading: boolean } {
  const { allowedTabs, isLoading } = useTabAccess()
  if (isLoading) return { hasAccess: false, isLoading: true }
  return { hasAccess: allowedTabs.includes(tabName), isLoading: false }
}
